import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { NumericInput } from '@components/core-ui/form-elements/NumericInput';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { OrganizationsService } from 'app/API';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Content } from './styles/Content';
import { Switch } from '@components/core-ui/form-elements/Switch';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  uid: string;
  concurrentJobsLimit: number;
};

export const EditConcurrentJobsLimitModal = ({ onCancel, onSuccess, uid, concurrentJobsLimit }: TProps) => {
  const { call } = useAPI();
  const { organization } = useAuthContext();
  const [newLimit, setNewLimit] = useState(concurrentJobsLimit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isConcurrentJobsLimitInvalid = newLimit !== 0 && (newLimit < 0 || newLimit > 100);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isConcurrentJobsLimitInvalid) {
      return;
    }
    setIsSubmitting(true);
    const response = await call(
      OrganizationsService.changeConcurrentJobsLimit({
        oid: organization?.id ?? '',
        memberId: uid,
        requestBody: { concurrentJobsLimit: newLimit },
      }),
    );
    setIsSubmitting(false);
    if (!response?.success) {
      toast.error(response?.message ?? 'Failed to update concurrent jobs limit');
      return;
    }
    onSuccess();
  };

  return (
    <ModalWrapper width="300px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Edit concurrent jobs limit</ModalTitle>
      <Content>
        <form action="#" onSubmit={submit}>
          <div className="pb-5">
            <Switch
              label="Unlimited"
              checked={newLimit === 0}
              onChange={() => setNewLimit(newLimit === 0 ? 1 : 0)}
              testId="unlimited-switch"
            />
          </div>
          {newLimit > 0 ? (
            <div className="inputWrapper">
              <NumericInput
                required
                value={newLimit}
                min={0}
                max={100}
                label="Concurrent jobs limit"
                name="concurrentJobsLimit"
                onChange={setNewLimit}
              />
            </div>
          ) : null}
          <div className="buttonWrapper">
            <OutlinedButton className="w-full" onClick={onCancel}>
              Cancel
            </OutlinedButton>
            <Button className="w-full" type="submit" disabled={isSubmitting || isConcurrentJobsLimitInvalid}>
              Save
            </Button>
          </div>
        </form>
      </Content>
    </ModalWrapper>
  );
};
